'use client';

import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Globe } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

export function SecuritySection() {
  const t = useTranslations('landing.security');

  const securityFeatures = [
    {
      icon: Lock,
      label: t('card1Label'),
      title: t('card1Title'),
      description: t('card1Desc'),
    },
    {
      icon: ShieldCheck,
      label: t('card2Label'),
      title: t('card2Title'),
      description: t('card2Desc'),
    },
    {
      icon: Globe,
      label: t('card3Label'),
      title: t('card3Title'),
      description: t('card3Desc'),
    },
  ];

  return (
    <section
      data-theme="dark"
      className="pt-20 pb-40 relative overflow-hidden bg-black rounded-t-[2rem] -mt-8 z-20"
    >
      <div className="container max-w-[1440px] mx-auto px-4 relative z-10">
        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-20" />

        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4 }}
            className="text-xs font-semibold uppercase tracking-widest text-primary mb-4"
          >
            {t('badge')}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.06 }}
            className="text-3xl md:text-5xl font-serif text-white mb-6"
          >
            {t('headline')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="text-lg text-white/80"
          >
            {t('subheadline')}
          </motion.p>
        </div>

        {/* Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="grid md:grid-cols-3 gap-8"
        >
          {securityFeatures.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="group relative flex flex-col p-10 rounded-3xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] hover:border-white/20 transition-all duration-300 backdrop-blur-md overflow-hidden"
              >
                {/* Top glow on hover */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />

                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-300">
                  <Icon strokeWidth={1.5} className="w-6 h-6" />
                </div>

                <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 mb-3">
                  {feature.label}
                </p>

                <h3 className="text-xl font-medium text-white mb-3 tracking-tight">
                  {feature.title}
                </h3>

                <p className="text-sm text-white/75 leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
